import {
  TextInput,
  CustomButton,
  CustomCheckbox,
  CustomSelect,
  CustomTooltip,
  ErrorMessage,
  CustomInput,
  RadioGroup,
  EthnicityDrawer,
} from '@/shared/ui/components';
import { riddleIcon } from '@/shared/ui/assets';
import { GENDER_OPTIONS } from '@/shared/config';
import { ETHNICITY_OPTIONS } from '@/entities/study';
import { gettextGender } from '@/shared/lib';
import _ from 'lodash';
import { t } from 'ttag';
import { useInformationDrawer } from './useInformationDrawer';
import type { InformationDrawerProps } from './InformationDrawer.types';
import './style.scss';

/**
 * Renders the patient demographic drawer covering first, middle and last name, patient ID, date of birth, gender, height in feet and inches, weight, ethnicity and implantable device flags, all bound to the useInformationDrawer state. Height errors collapse into a single message for either field, the pacemaker checkbox maps to the 'Yes'/'No' pacer value, and the ethnicity title opens an explanatory ethnicity drawer.
 *
 * @param data - Existing patient information used to seed the form.
 * @param title - Drawer section title passed through to the drawer hook.
 * @param handleClick - Callback closing the drawer, also wired to the cancel button.
 * @param isSaving - Whether a save request is in flight, disabling cancel and loading save.
 * @returns The patient information editing form with action buttons.
 */
const InformationDrawer = ({ data, title, handleClick, isSaving }: InformationDrawerProps) => {
  const { state, handleChange, onClickBottomButton, toggleEthnicityDrawer } = useInformationDrawer({
    data,
    title,
    handleClick,
  });

  return (
    <div className="list-input">
      <EthnicityDrawer
        open={state.isOpenEthnicityDrawer}
        onCancel={toggleEthnicityDrawer}
        width={500}
      />

      <CustomInput
        size="middle"
        title={t`First name`}
        name="firstName"
        value={state.firstName}
        onChange={handleChange}
        isError={!!state.errorObject.firstName}
        errorMessage={state.errorObject.firstName}
        placeholder={t`Enter patient’s first name`}
      />
      <CustomInput
        size="middle"
        title={t`Middle name (optional)`}
        name="middleName"
        value={state.middleName}
        onChange={handleChange}
        placeholder={t`Enter patient’s middle name`}
      />
      <CustomInput
        size="middle"
        title={t`Last name`}
        name="lastName"
        value={state.lastName}
        onChange={handleChange}
        placeholder={t`Enter patient’s last name`}
        isError={!!state.errorObject.lastName}
        errorMessage={state.errorObject.lastName}
      />

      <CustomInput
        size="middle"
        title={t`Patient ID`}
        name="patientId"
        value={state.patientId}
        onChange={handleChange}
        placeholder={t`Enter patient ID`}
        isError={!!state.errorObject.patientId}
        errorMessage={state.errorObject.patientId}
      />
      <TextInput
        isDateMask
        name="dob"
        label={t`Date of birth`}
        placeholder={t`MM/DD/YYYY`}
        value={state.dob}
        onChange={handleChange}
        isError={!!state.errorObject.dob}
        errorMessage={state.errorObject.dob}
      />
      <div>
        <span className="custom-text-input__label">{t`Gender`}</span>
        <div className="mt1">
          <RadioGroup
            className="radio-input"
            value={state.gender}
            name="gender"
            onChange={handleChange}
            options={_.map(GENDER_OPTIONS, (item) => ({
              label: gettextGender(item.label),
              value: item.value,
            }))}
            size="small"
          />
        </div>
      </div>
      <div>
        <span className="custom-text-input__label">{t`Height`}</span>
        <div className="flex mt1 justify-between">
          <TextInput
            name="heightFt"
            className="w-100 mr1"
            isNumberFormat
            value={state.heightFt}
            onChange={handleChange}
            placeholder={t`Enter a number`}
            suffix="ft"
            decimalSeparator={false}
            isError={!!state.errorObject.heightFt}
          />
          <TextInput
            name="heightIn"
            className="w-100 ml1"
            isNumberFormat
            value={state.heightIn}
            onChange={handleChange}
            placeholder={t`Enter a number`}
            suffix="in"
            decimalSeparator={false}
            isError={!!state.errorObject.heightIn}
          />
        </div>
        {(!!state.errorObject.heightFt || !!state.errorObject.heightIn) && (
          <ErrorMessage
            errorMessage={state.errorObject.heightFt || state.errorObject.heightIn}
            hasIcon
          />
        )}
      </div>
      <div>
        <TextInput
          name="weight"
          label={t`Weight`}
          isNumberFormat
          value={state.weight}
          onChange={handleChange}
          isError={!!state.errorObject.weight}
          errorMessage={state.errorObject.weight}
          placeholder={t`Enter a number (e.g., 176.5)`}
          suffix="lb"
          decimalScale={1}
        />
      </div>

      <div>
        <div className="optional-title">
          <button type="button" className="ethnicity-btn" onClick={toggleEthnicityDrawer}>
            <span className="title">{t`Ethnicity`}</span>
            <CustomTooltip title={t`Click to learn more about ethnicity`}>
              <img src={riddleIcon} alt="riddleIcon" />
            </CustomTooltip>
          </button>
        </div>
        <CustomSelect
          placeholder={`${t`Select`}...`}
          name="ethnicity"
          size="middle"
          options={ETHNICITY_OPTIONS}
          showSearch
          allowClear
          value={state.ethnicity}
          onChange={handleChange}
          id="ethnicity-select"
          aria-label={t`Ethnicity`}
        />
      </div>

      <div>
        <span className="custom-text-input__label">{t`Implantable device`}</span>
        <div className="implantable_device">
          <CustomCheckbox
            className="custom-checkbox"
            checked={state.pacer === 'Yes'}
            onChange={(checked) => {
              handleChange('pacer', checked ? 'Yes' : 'No');
            }}
            label={t`Pacemaker`}
          />
          <CustomCheckbox
            className="custom-checkbox"
            checked={state.icd}
            onChange={(checked) => handleChange('icd', checked)}
            label={t`ICD`}
          />
        </div>
      </div>

      <div className="bottom-button-row">
        <CustomButton
          id="cancel-btn"
          style="neutral"
          type="outline"
          onClick={handleClick}
          styles={{ minWidth: 80 }}
          disabled={isSaving}
        >
          {t`Cancel`}
        </CustomButton>
        <CustomButton
          id="save-btn"
          onClick={onClickBottomButton}
          styles={{ minWidth: 80 }}
          loading={isSaving}
        >
          {t`Save`}
        </CustomButton>
      </div>
    </div>
  );
};

export default InformationDrawer;
